import { APIResponse, GETResponse, PUTRequestBody, PaginatedResponse } from '../../helpers'
import { IEmployee } from '../employees'
import { IVehicle } from '../vehicles'
import { IWorkorder } from '../workorder'

export interface IClockingAction {
  UUID: string
  ShopID: string
  EmployeeUUID: string
  ClockedInAt: string
  ClockedOutAt: string
  Type: ClockingType
  TypeDescription: string
  TotalClockedSeconds: number
  ClockingItemUUID?: string
  WorkorderUUID?: string
}

export enum ClockingType {
  Break = 'break',
  Day = 'day',
  Item = 'item',
  Workorder = 'workorder',
  WorkorderItem = 'workorder_item'
}

export interface IClockingActionData {
  Day: IClockingAction | null
  Action: IClockingAction | null
  ServerTime: string
}

export interface IClockingActionsResponseRow {
  ClockingAction: IClockingAction
  Employee: IEmployee
  InvoiceVehicle: IVehicle
  InvoiceWorkorder: IWorkorder
  Vehicle: IVehicle
  Workorder: IWorkorder
}

export interface IWorkorderClockingAction {
  ClockingAction: IClockingAction
  Employee: IEmployee
}

export interface ClockingYearList {
  Year: number[]
}

///
export interface ReportData {
  quarter?: string
  month?: string
  week?: string
  day?: string
  TimePeriod: string
  TotalHours: number
  TotalWorkOrderHrs: number
  TotalGeneralHrs: number
  TotalBreakHrs: number
  TotalIdleHrs: number
  Productivity: number
  ClockedIn: string
  ClockedOut: string
}

export interface Summary {
  WorkorderPercentage: number
  GeneralPercentage: number
  BreakPercentage: number
  IdlePercentage: number
  AggregatedProductivity: number
}

export interface TotalAggregatedSummary {
  TotalWorkorderHours: number
  TotalGeneralHours: number
  TotalBreakHours: number
  TotalHours: number
  TotalIdleHours: number
  AggregatedProductivity: number
}

export interface ProductivityReportResponse {
  ReportType: string
  startDate: string
  endDate: string
  ReportData: ReportData[]
  Summary: Summary
}

export interface IProductivityTableData {
  originalData: ReportData[]
  tableHeaders: string[]
  refinedData: ReportData[]
  reportType: string
  donutSummary: Summary
  reportTotalSummary: TotalAggregatedSummary
  employeeName: string
}

export interface TotalAggregatedEfficiencySummary {
  TotalHoursWorked: number
  TotalBillableHours: number
  AggregatedEfficiency: number
}

export interface EfficiencyReportData {
  TimePeriod: string
  WorkorderId?: string
  WorkorderUUID?: string | undefined
  WorkorderInvoiceUUID?: string | undefined
  TotalHoursWorked: number
  TotalBillableHours: number
  Efficiency: number
  week?: string
}

export interface EfficiencyReportResponse {
  ReportType: string
  startDate: string
  endDate: string
  ReportData: EfficiencyReportData[]
  AggregatedEfficiency: number
}

export interface IEfficiencyTableData {
  originalData: EfficiencyReportData[]
  tableHeaders: string[]
  refinedData: EfficiencyReportData[]
  reportType: string
  employeeName: string
  AggregatedEfficiency: number
  reportTotalSummary: TotalAggregatedEfficiencySummary
}

export interface IAutomatedBreakSettings {
  Enabled: boolean
}

export type Level = 0 | 1 | 2 | 3 | 4

export type ChartDataSeries =
  | (ReportData & { Efficiency?: never; WorkorderId?: never }) // ReportData cannot have Efficiency
  | (EfficiencyReportData & { Productivity?: never }) // EfficiencyReportData cannot have Productivity

export type GETProductivityReportResponse = GETResponse<ProductivityReportResponse>

export type GETEfficiencyReportResponse = GETResponse<EfficiencyReportResponse>

export type GETClockingActionYearListResponse = GETResponse<ClockingYearList>

export type GETClockingActionResponse = GETResponse<IClockingActionData>

export type GETClockingActionsResponse = PaginatedResponse<IClockingActionsResponseRow>

export interface POSTActionClockInBody
  extends Pick<IClockingAction, 'Type'>,
    Partial<Pick<IClockingAction, 'ClockingItemUUID' | 'WorkorderUUID'>> {}

export type POSTActionClockInResponse = APIResponse<IClockingActionData>

export type PUTActionClockOutBody = Pick<IClockingAction, 'UUID'>
export type PUTActionClockOutResponse = APIResponse<IClockingActionData>

export type GETWorkOrderClockingActionsResponse = PaginatedResponse<IWorkorderClockingAction>

export type PUTAutomatedBreakSettingsBody = PUTRequestBody<IAutomatedBreakSettings>

export type GETAutomatedBreakSettingsResponse = GETResponse<IAutomatedBreakSettings>
